import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { ServiceCategory, ServiceItem } from 'orderme-api-integration-client';
import { appStyleMode } from '../../helpers/clientConfigs';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { useTranslation } from 'react-i18next';
import { GoodsRoute } from '../../routes/allRoutes';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { ServiceClient } from '../../helpers/client';
import { roundDoubleNumbers } from '../../helpers/currency';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { AppStyle } from '../../helpers/appStyle';
import { getUserId } from '../../helpers/customerHelper';
import { ServiceItemImage } from '../serviceItemView/serviceItemImage';
import { Linear } from '../progressBars/linear';
import { EmptyGoodsList } from './emptyGoodsList';
import { RootState } from '../../redux/store';
import { addToCartAsync } from '../../redux/reducers/shoppingCartReducer';
import { ButtonLoadingIndicator } from '../../controls';

export function GoodsList() {
  const [goods, setGoods] = useState<ServiceItem[]>();
  const servicesClient = useMemo(() => new ServiceClient(), []);
  const { t } = useTranslation();
  const routeNavigator = useRouteNavigator();
  const dispatch = useAppDispatch();
  const goodsCategory: ServiceCategory = useAppSelector((state: RootState) => state.categoriesState.goodsCategory);
  const updatingCart = useAppSelector((state: RootState) => state.shoppingCartState.updatingCart);

  const fetchGoods = useCallback(async () => {
    try {
      const serviceItems = await servicesClient.getServiceItems(getUserId());
      setGoods(serviceItems);
    } catch (error) {
      console.error('Error fetching goods:', error);
    }
  }, [servicesClient]);

  useEffect(() => {
    fetchGoods();
  }, [fetchGoods]);

  const openGoodsItem = (goodsItem: ServiceItem) => {
    routeNavigator.goToGoodsItem(goodsItem);
  };

  const addProductToCart = (service: ServiceItem) => {
    dispatch(addToCartAsync(service.id));
  };

  return (
    <Box sx={{}} className={appStyleMode === AppStyle.Mobile && 'goods-list'}>
      <BreadcrumbsBar paths={[{ path: GoodsRoute, title: goodsCategory?.name }]} />
      {!goods && (
        <Box
          sx={{
            height: '600px',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'white',
            borderRadius: '15px',
            pt: '10px',
          }}
        >
          <Linear />
        </Box>
      )}
      {goods &&
        (goods.length === 0 ? (
          <EmptyGoodsList />
        ) : (
          <Grid
            container
            sx={{
              px: '10px',
              width: 'auto',
              pb: '20px',
              backgroundColor: 'white',
              borderRadius: '15px',
              py: '10px',
              color: 'black',
            }}
          >
            {goods.map((item, index) => (
              <Grid sx={{ px: '5px', pb: '20px' }} size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                <Grid container sx={{ flexDirection: 'column', height: '100%' }}>
                  <Box
                    sx={{
                      maxHeight: '300px',
                      '&:hover': {
                        cursor: item.logoId && 'pointer',
                      },
                    }}
                    onClick={() => openGoodsItem(item)}
                  >
                    {item.logoId && <ServiceItemImage service={item} />}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                        },
                      }}
                      onClick={() => openGoodsItem(item)}
                    >
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Typography
                        sx={{
                          opacity: 0.7,
                          textDecoration: 'line-through',
                          pr: '10px',
                          display: item.originalPrice - item.price === 0 ? 'none' : 'inline-block',
                        }}
                      >
                        {roundDoubleNumbers(item.originalPrice)} €
                      </Typography>
                      <Typography> {roundDoubleNumbers(item.price)} €</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                    <Button
                      onClick={() => addProductToCart(item)}
                      disabled={updatingCart}
                      loading={updatingCart}
                      loadingIndicator={<ButtonLoadingIndicator size={20} />}
                    >
                      {t('payment:addToCart')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ))}
          </Grid>
        ))}
    </Box>
  );
}
